/**
 * Cache Configuration
 * Cấu hình tập trung cho tất cả các loại cache trong hệ thống
 */

import { CacheOptions } from './types';

/**
 * Cache configuration cho các loại dữ liệu khác nhau
 * Mỗi entry định nghĩa key, TTL, và behavior cho một loại data
 */
export const CacheConfig: Record<string, CacheOptions> = {
  /**
   * Branding Configuration - Logo, tên công ty, theme
   * TTL: 5 phút
   * Refresh: Có (background refresh sau khi render)
   */
  BRANDING: {
    key: 'branding_config',
    ttl: 5 * 60 * 1000, // 5 minutes
    refreshAfterRender: true,
  },
  
  /**
   * JWT Token - Authentication token
   * TTL: 24 giờ (hoặc theo JWT expiry từ backend)
   * Refresh: Không (token mới khi login lại)
   */
  AUTH_TOKEN: {
    key: 'auth_token',
    ttl: 24 * 60 * 60 * 1000, // 24 hours
    refreshAfterRender: false,
  },
  
  /**
   * User Profile - Thông tin user hiện tại
   * TTL: 15 phút
   * Refresh: Có
   */
  USER_PROFILE: {
    key: 'user_profile',
    ttl: 15 * 60 * 1000, // 15 minutes
    refreshAfterRender: true,
  },
  
  /**
   * Permissions & Roles - Quyền và vai trò của user
   * TTL: 15 phút
   * Refresh: Có
   */
  PERMISSIONS: {
    key: 'user_permissions',
    ttl: 15 * 60 * 1000, // 15 minutes
    refreshAfterRender: true,
  },
  
  /**
   * Tenant Information - Thông tin tenant hiện tại
   * TTL: 10 phút
   * Refresh: Có
   */
  TENANT_INFO: {
    key: 'tenant_info',
    ttl: 10 * 60 * 1000, // 10 minutes
    refreshAfterRender: true,
  },
  
  /**
   * Tenant List - Danh sách tất cả tenants
   * TTL: 10 phút
   * Refresh: Có
   */
  TENANT_LIST: {
    key: 'tenant_list',
    ttl: 10 * 60 * 1000, // 10 minutes
    refreshAfterRender: true,
  },
  
  /**
   * Provider List - Danh sách providers (streaming, storage, etc.)
   * TTL: 10 phút
   * Refresh: Có
   */
  PROVIDER_LIST: {
    key: 'provider_list',
    ttl: 10 * 60 * 1000, // 10 minutes
    refreshAfterRender: true,
  },
  
  /**
   * Config Options - Các cấu hình hệ thống
   * TTL: 30 phút
   * Refresh: Có
   */
  CONFIG_OPTIONS: {
    key: 'config_options',
    ttl: 30 * 60 * 1000, // 30 minutes
    refreshAfterRender: true,
  },
  
  /**
   * UI Preferences - Tùy chỉnh giao diện người dùng (theme, language, etc.)
   * TTL: Vô hạn (chỉ clear khi logout hoặc reset)
   * Refresh: Không
   */
  UI_PREFERENCES: {
    key: 'ui_preferences',
    ttl: Infinity, // Không expire
    refreshAfterRender: false,
  },

  /**
   * System Configuration - Cấu hình hệ thống (SystemService/GetSystemConfig)
   * Dùng ở màn System > Configuration và Issuance Plans (tạo kế hoạch loại vé)
   * TTL: 30 phút — cân bằng giữa giảm API call và độ trễ thấy config mới khi
   * admin khác đổi cấu hình từ session/máy khác (localStorage không đồng bộ cross-session)
   * Refresh: Không tự động — ghi đè thủ công (write-through) ngay sau khi update thành công
   */
  SYSTEM_CONFIG: {
    key: 'system_config', // → localStorage key thực tế: lms_cache_system_config
    ttl: 30 * 60 * 1000, // 30 minutes
    refreshAfterRender: false,
  },

  /**
   * Active Ticket Types - Danh sách loại vé đang mở (ticket-service)
   * Dùng ở màn Issuance Plans (dropdown loại vé, tính số chữ số/ký hiệu khi lập kế hoạch)
   * TTL: 15 phút — danh sách loại vé ít khi đổi, trước đây màn Issuance Plans gọi lại API này
   * mỗi lần mount nên vẫn chậm dù SystemConfig (số lượng vé/kỳ) đã cache.
   * Refresh: Không tự động — ghi đè thủ công khi tạo/sửa/khóa loại vé ở TicketTypeManagement
   */
  ACTIVE_TICKET_TYPES: {
    key: 'active_ticket_types', // → localStorage key thực tế: lms_cache_active_ticket_types
    ttl: 15 * 60 * 1000, // 15 minutes
    refreshAfterRender: false,
  },

  /**
   * Draw Result File - Kết quả quay số public trên MinIO (DrawPrize/{issuancePlanId}.json)
   * Dùng ở LookupPortal/TicketLookup để check vé trúng thưởng — key thực tế phải nối thêm
   * issuancePlanId (VD: `${CacheConfig.DRAW_RESULT.key}_${issuancePlanId}`) vì mỗi kỳ 1 file riêng.
   * TTL: 1 ngày — kết quả 1 kỳ không đổi sau khi đã công bố.
   * Refresh: Không tự động — hết TTL thì tự fetch lại ở lần check tiếp theo.
   */
  DRAW_RESULT: {
    key: 'draw_result', // → localStorage key thực tế: lms_cache_draw_result_{issuancePlanId}
    ttl: 24 * 60 * 60 * 1000, // 1 ngày
    refreshAfterRender: false,
  },

  /**
   * Issuance Plan Config - Top 5 kỳ phát hành gần nhất (theo CreatedAt), file public trên MinIO
   * (pmxs/IssuancePlan/issuance-plan-config.json — ghi đè mỗi khi có kỳ mới, xem
   * CreateIssuancePlanCommand.cs bên ticket-service). Dùng ở LookupPortal thay cho gọi API mỗi lần
   * tra cứu — giảm tải server khi nhiều người cùng tra cứu (xem README lmsapp-service mục 7.1).
   * TTL: 5 phút — đủ ngắn để thấy kỳ mới tạo tương đối nhanh, đủ dài để giảm tải request lặp lại.
   * Refresh: Không tự động — hết TTL thì tự fetch lại ở lần tra cứu tiếp theo.
   */
  ISSUANCE_PLAN_CONFIG: {
    key: 'issuance_plan_config', // → localStorage key thực tế: lms_cache_issuance_plan_config
    ttl: 5 * 60 * 1000, // 5 phút
    refreshAfterRender: false,
  },
} as const;

/**
 * Global cache settings
 * Cấu hình chung cho toàn bộ cache system
 */
export const GlobalCacheSettings = {
  /** Prefix cho tất cả cache keys trong LocalStorage */
  prefix: 'lms_cache_',
  
  /** Giới hạn tổng size của cache (bytes) */
  maxSize: 5 * 1024 * 1024, // 5MB max
  
  /** Interval để cleanup expired entries (milliseconds) */
  cleanupInterval: 60 * 60 * 1000, // Cleanup mỗi giờ
  
  /** Enable debug logging (chỉ trong development) */
  enableDebug: process.env.NODE_ENV === 'development',
  
  /** Cache version - increment khi thay đổi cache schema */
  version: '1.0.0',
} as const;